import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { PresetManager } from "@bryntum/schedulerpro";
import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { getScheduleInstanceFromViewManager } from "@/other/utils/otherUtils";
import { Card } from "@/modern/components/ui/card";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

const TIMESCALES = [
  {
    id: "manyYears",
    name: "Years",
    timelineFormat: "22, 23, 24, 25, 26, 27 ..."
  },
  {
    id: "year",
    name: "Quarters",
    timelineFormat: "Q1, Q2, Q3, Q4, Q1, Q2, Q3, Q4 ..."
  },
  {
    id: "monthAndYear",
    name: "Months",
    timelineFormat: "June 2024, July 2024, August 2024 ..."
  },
  {
    id: "weekDateAndMonth",
    name: "Weeks",
    timelineFormat: "01, 08, 15, 22, 29, 05, 12, 19 ..."
  },
  {
    id: "weekAndMonth",
    name: "Weeks",
    timelineFormat: "May 01, May 08, May 15, May 22 ..."
  },
  {
    id: "weekAndDayLetter",
    name: "Days",
    timelineFormat: "S, M, T, W, T, F, S ..."
  },
  {
    id: "weekAndDay",
    name: "Days",
    timelineFormat: "06 May, 07 May, 08 May, 09 May ..."
  },
  {
    id: "dayAndMonth",
    name: "Days",
    timelineFormat: "06, 07, 08, 09, 10, 11, 12, 13, 14 ..."
  },
  {
    id: "dayAndWeek",
    name: "Days",
    timelineFormat: "Su 06, Mo 07, Tu 08, We 09, Th 10 ..."
  },
  {
    id: "hourAndDay",
    name: "Hours",
    timelineFormat: "1 AM, 2 AM, 3 AM, 4 AM, 5 AM ..."
  },
  {
    id: "minuteAndHour",
    name: "1/2 Hours",
    timelineFormat: "00, 30, 00, 30, 00, 30, 00, 30 ..."
  },
  {
    id: "secondAndMinute",
    name: "Minutes",
    timelineFormat: "00, 10, 20, 30, 40, 50, 00, 10 ..."
  }
];

export const TimescalesTab = () => {
  const context = useSwivuBryntumContext();
  const { viewManager } = context;
  const gridRef = useRef<any>(null);

  const schedulerView = useMemo(() => {
    return getScheduleInstanceFromViewManager(viewManager);
  }, [viewManager]);

  // Initialize presets data
  const presets = useMemo(() => {
    return TIMESCALES.map((timeScale) => ({
      id: timeScale.id,
      name: timeScale.name,
      timeResolution: timeScale.timelineFormat,
      gridRadio: false
    }));
  }, []);

  useEffect(() => {
    if (schedulerView && gridRef.current?.instance) {
      const grid = gridRef.current.instance;
      grid.swivuContext = context;

      // Set the store data
      grid.store.data = presets;

      const currentPreset = schedulerView.viewPreset;
      let presetId = currentPreset.id;

      // The selected preset may be a variant of base preset, extract the base preset id
      if (presetId.includes("-")) {
        const [basePresetId] = presetId.split("-");
        presetId = basePresetId;
      }

      // Update grid radio button for current preset
      const currentPresetRow = grid.store.getById(presetId);
      if (currentPresetRow) {
        currentPresetRow.gridRadio = true;
      }
    }
  }, [schedulerView, presets]);

  const selectionChange = useCallback(({ source, checked, userAction }: { source: any; checked: boolean; userAction: boolean }) => {
    if (userAction && checked) {
      const { record } = source.cellInfo;
      const grid = gridRef.current?.instance;
      if (grid && schedulerView) {
        const selectedRecord = grid.store.getById(record.id);
        schedulerView.viewPreset = PresetManager.getById(selectedRecord.id);

        /* Todo: We need to check why the `swivuLastViewportCenterDate` is always null */
        if (grid.swivuContext?.currentViewRef?.current?.swivuLastViewportCenterDate) {
          schedulerView.scrollToDate(grid.swivuContext?.currentViewRef?.current?.swivuLastViewportCenterDate, { block: "center" });
        }
      }
    }
  }, [schedulerView, gridRef.current]);

  const gridConfig = useMemo(() => {
    return {
      height: "100%",
      stateId: PERSISTENCE_STATE_KEYS.timelineConfiguratorEditorTimescaleGridState,
      features: {
        cellMenu: false,
        headerMenu: false,
        sort: false,
        group: false
      },
      selectionMode: {
        multiSelect: false,
        cell: false,
        column: false
      },
      columns: [
        {
          type: "widget",
          text: "",
          id: "checkRadio",
          width: "1em",
          widgets: [
            {
              type: "radio",
              name: "gridRadio",
              color: "b-blue",
              cls: "swivu-timeScale-radio !mx-[auto]",
              listeners: {
                change: selectionChange
              }
            }
          ]
        },
        {
          id: "timescale",
          field: "name",
          text: "Timescale",
          flex: 1,
          editor: false
        },
        {
          id: "timescaleFormat",
          field: "timeResolution",
          text: "Timescale Format",
          flex: 1,
          editor: false
        }
      ]
    }
  }, [selectionChange]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 h-full">
          <div className="max-w-full h-full">
            <Card className="py-3 px-4 h-full">
              <div className="space-y-6 h-full flex flex-col">
                <div>
                  <h3 className="text-base font-semibold text-foreground mb-1">Timeline Configuration</h3>
                  <p className="text-xs text-muted-foreground">Choose how time is displayed in the schedule view</p>
                </div>

                {/* @ts-expect-error - BryntumGrid types are not fully compatible */}
                <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuGrid} config={gridConfig} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
